import { Link } from 'react-router-dom';
import { Clock, Tag, TrendingUp } from 'lucide-react';
import type { Item } from '../types';
import { PriceTierBadge } from './PriceTierBadge';

interface ItemCardProps {
  item: Item;
}

export function ItemCard({ item }: ItemCardProps) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const confidencePercent = Math.round((item.confidence_score || 0) * 100);

  return (
    <Link
      to={`/items/${item.id}`}
      className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 p-5 hover:shadow-md dark:hover:shadow-dark-900/50 transition-shadow group"
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-500 transition-colors">
            {item.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{item.category}</p>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
          <TrendingUp className="w-3.5 h-3.5" />
          <span>{confidencePercent}%</span>
        </div>
      </div>

      {/* Price Tiers */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <PriceTierBadge tier="low" price={item.low_price} size="sm" />
        <PriceTierBadge tier="medium" price={item.medium_price} size="sm" />
        <PriceTierBadge tier="high" price={item.high_price} size="sm" />
      </div>

      {/* Tags */}
      {item.tags && item.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {item.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 text-xs rounded-full"
            >
              <Tag className="w-3 h-3" />
              {tag}
            </span>
          ))}
          {item.tags.length > 3 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">+{item.tags.length - 3}</span>
          )}
        </div>
      )}

      {/* Last Updated */}
      <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
        <Clock className="w-3.5 h-3.5" />
        <span>Updated {formatDate(item.last_updated)}</span>
      </div>
    </Link>
  );
}
